/**
 * One place to reach everything, opened with Cmd+K.
 *
 * Xero puts every "create" action in a single keyboard-annotated command list
 * on the global nav rather than scattering "+ New X" buttons per page (L §1,
 * items 2 and 3), and the discoverability win is most of the value: an operator
 * finds a screen they did not know existed by typing three letters of it.
 *
 * THE NAV TABLE IS THE SOURCE. `commandEntries` reads `ADMIN_NAV_ITEMS` (and
 * `SYSTEM_SECTIONS`, the screens behind the System entry), so a sidebar entry
 * added without touching this file is in the palette. A hardcoded
 * copy would drift, and a palette that silently lacks the newest screen is
 * worse than none.
 *
 * NO PORTAL, AND THAT IS DELIBERATE. `components/ui/` has no `command` or
 * `dialog` primitive; `sheet.tsx` is the only Base UI dialog here and it
 * renders through a portal, which puts nothing in the markup a
 * `renderToStaticMarkup` test can read — and this console's tests have no DOM.
 * So the palette is an inline fixed overlay, rendered only when open: no new
 * dependency, and every part of it visible to the suite. If a `command`
 * primitive is ever added, this is the one file to change.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Input } from "@/components/ui/input";
import { companyDisplayName } from "@/lib/company-name";
import { paletteCompaniesQuery, paletteUsersQuery } from "@/lib/queries";
import { cn } from "@/lib/utils";
import type { CompanyRow, UserRow } from "@/types";

import { ADMIN_NAV_ITEMS } from "./AdminSidebar";
import { SYSTEM_SECTIONS } from "./system-tabs";

export type CommandGroup = "Go to" | "Create new" | "Users" | "Companies";

export interface CommandEntry {
  id: string;
  label: string;
  group: CommandGroup;
  to: string;
}

/**
 * The four things an operator creates, and the tab each one lives on.
 *
 * `?new=1` is a URL contract, not a hint: plan 15's rule, contact, account and
 * journal editors open on it, so the palette navigates and the tab does the
 * rest. A tab that does not read it simply opens, which is the right failure.
 */
const CREATE_ACTIONS: ReadonlyArray<{ label: string; slug: string }> = [
  { label: "New journal", slug: "journals" },
  { label: "New rule", slug: "rules" },
  { label: "New contact", slug: "contacts" },
  { label: "New account", slug: "chart" },
];

export function commandEntries(bookId: string | null): CommandEntry[] {
  const goTo: CommandEntry[] = [
    ...ADMIN_NAV_ITEMS.map((item) => ({
      id: `nav:${item.to}`,
      label: item.title,
      group: "Go to" as const,
      to: item.to,
    })),
    // The screens folded under System's single sidebar entry stay findable by
    // their own names: typing "fax" still opens Fax.
    ...SYSTEM_SECTIONS.map((section) => ({
      id: `system:${section.to}`,
      label: section.label,
      group: "Go to" as const,
      to: section.to,
    })),
  ];

  // A create action needs a book, and offering one with nowhere to go is a
  // dead entry: say what is missing instead.
  const create: CommandEntry[] = bookId
    ? CREATE_ACTIONS.map((action) => ({
        id: `new:${action.slug}`,
        label: action.label,
        group: "Create new",
        to: `/accounting/books/${encodeURIComponent(bookId)}/${action.slug}?new=1`,
      }))
    : [
        {
          id: "new:pick-book",
          label: "Pick a book first — open Accounting",
          group: "Create new",
          to: "/accounting",
        },
      ];

  return [...goTo, ...create];
}

export function filterCommands(
  entries: CommandEntry[],
  q: string,
): CommandEntry[] {
  const needle = q.trim().toLowerCase();
  if (needle === "") return entries;
  return entries.filter((entry) => entry.label.toLowerCase().includes(needle));
}

const GROUPS: readonly CommandGroup[] = [
  "Users",
  "Companies",
  "Go to",
  "Create new",
];

/** A search shorter than this would match half the directory. */
const MIN_LOOKUP_LENGTH = 2;

/** Users and companies the server matched for the typed text, as palette rows. */
export function lookupEntries(
  users: readonly UserRow[],
  companies: readonly CompanyRow[],
): CommandEntry[] {
  const userRows = users.flatMap((user): CommandEntry[] => {
    if (!user.auth_user_id) return [];
    const name = [user.firstname, user.lastname].filter(Boolean).join(" ");
    return [
      {
        id: `user:${user.auth_user_id}`,
        label:
          [name, user.email].filter(Boolean).join(" · ") || user.auth_user_id,
        group: "Users",
        to: `/users/${encodeURIComponent(user.auth_user_id)}`,
      },
    ];
  });
  const companyRows = companies.flatMap((company): CommandEntry[] =>
    company.company_id
      ? [
          {
            id: `company:${company.company_id}`,
            label: companyDisplayName(company),
            group: "Companies",
            to: `/companies/${encodeURIComponent(company.company_id)}`,
          },
        ]
      : [],
  );
  return [...userRows, ...companyRows];
}

/** The order the list draws in, so arrow keys follow what is on screen. */
export function orderByGroup(entries: CommandEntry[]): CommandEntry[] {
  return GROUPS.flatMap((group) =>
    entries.filter((entry) => entry.group === group),
  );
}

export function CommandPaletteList({
  entries,
  activeIndex,
  onPick,
}: {
  entries: CommandEntry[];
  activeIndex: number;
  onPick?: (entry: CommandEntry) => void;
}) {
  if (entries.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-sm text-muted-foreground">
        No matches.
      </p>
    );
  }

  return (
    <ul role="listbox" className="max-h-80 overflow-y-auto py-1">
      {GROUPS.map((group) => {
        const rows = entries.filter((entry) => entry.group === group);
        if (rows.length === 0) return null;
        return (
          <li key={group}>
            <span className="block px-3 py-1 text-compact font-medium text-muted-foreground">
              {group}
            </span>
            <ul>
              {rows.map((entry) => {
                const active = entries.indexOf(entry) === activeIndex;
                return (
                  <li
                    key={entry.id}
                    role="option"
                    aria-selected={active}
                    className={cn(
                      "mx-1 cursor-pointer rounded-control px-2 py-1.5 text-sm text-body",
                      active && "bg-surface text-foreground",
                    )}
                    onClick={() => onPick?.(entry)}
                  >
                    {entry.label}
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { bookId } = useParams();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const needle = query.trim();
  const lookup = needle.length >= MIN_LOOKUP_LENGTH;
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(lookup ? needle : ""), 200);
    return () => clearTimeout(timer);
  }, [needle, lookup]);
  const users = useQuery({
    ...paletteUsersQuery(debounced),
    enabled: open && debounced !== "",
  });
  const companies = useQuery({
    ...paletteCompaniesQuery(debounced),
    enabled: open && debounced !== "",
  });

  const entries = orderByGroup([
    ...(debounced === needle
      ? lookupEntries(users.data?.data ?? [], companies.data?.data ?? [])
      : []),
    ...filterCommands(commandEntries(bookId ?? null), query),
  ]);
  const searching =
    lookup &&
    (debounced !== needle || users.isFetching || companies.isFetching);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((was) => !was);
        setQuery("");
        setActive(0);
      }
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (!open) return null;

  const pick = (entry: CommandEntry) => {
    setOpen(false);
    navigate(entry.to);
  };

  const onListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, entries.length - 1));
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    }
    if (event.key === "Enter" && entries[active]) pick(entries[active]!);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-scrim px-4 pt-24 supports-backdrop-filter:backdrop-blur-xs"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-menu bg-popover shadow-panel"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onListKeyDown}
      >
        <div className="border-b p-2">
          <Input
            aria-label="Search commands"
            autoFocus
            placeholder="Find a user by email, a company, or a screen…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
          />
        </div>
        <CommandPaletteList
          entries={entries}
          activeIndex={active}
          onPick={pick}
        />
        {searching ? (
          <p className="px-3 py-1.5 text-sm text-muted-foreground">
            Searching users and companies…
          </p>
        ) : null}
        <p className="border-t px-3 py-1.5 text-sm text-muted-foreground">
          ↑↓ to move, Enter to open, Esc to close, Cmd+K to reopen.
        </p>
      </div>
    </div>
  );
}
